import { useRef, useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { Button, Card, CardContent, IconButton } from "@mui/material"
import { KeyboardArrowDown, CloudDownload } from "@mui/icons-material"
import Markdown from "markdown-to-jsx"
import "./style.scss"

export default function ApproveAccount({ terms, termsVersion, acceptTermsAndConditions, account, hasError, termsLicense }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false)
  const endRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!terms) {
      navigate(`/${account}/home`, { replace: true })
    }
  }, [terms, navigate, account])

  const logout = () => {
    window.location = "/logout"
  }

  const accept = () => {
    acceptTermsAndConditions(account)
  }

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 30
    if (bottom) {
      setScrolledToBottom(true)
    }
  }

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: "smooth" })
    setScrolledToBottom(true)
  }

  const [reviewText, agreeText, acceptText, denyText, titleText, fileName] = useMemo(() => {
    if (termsLicense === "alpha_theory") {
      return [
        "Please review the following permissions by scrolling to the bottom of the document. You must accept these permissions before continuing the enrollment process.",
        'By clicking on the "AUTHORIZE" button, you are indicating that you have read and agree to the above Alpha Theory Application permissions.',
        "Authorize",
        "No Thanks",
        "Authorize Alpha Theory Application",
        "Alpha_Theory_Omega_Point_Integration_Permissions",
      ]
    } else if (termsLicense === "eze") {
      return [
        "Please review the following permissions by scrolling to the bottom of the document. You must accept these permissions before continuing the enrollment process.",
        'By clicking on the "AUTHORIZE" button, you are indicating that you have read and agree to the above Eze Eclipse Application permissions.',
        "Authorize",
        "No Thanks",
        "Authorize Eze Eclipse Application",
        "Eze_Integration_Permissions",
      ]
    }

    return [
      "Please review the terms and conditions by scrolling to the bottom of the document. You must accept these terms and conditions before continuing the enrollment process.",
      'By clicking on the "I ACCEPT" button, you are indicating that you have read and agree to the above terms and conditions.',
      "I Accept",
      "I Do Not Accept",
      "Getting started with Omega Point",
      "Omega_Point_Evaluation_Agreement",
    ]
  }, [termsLicense])

  return <div className="modules_approve-account">
    <Card className="content-wrapper" elevation={0} square>
      <CardContent className="approve-account">
        <div className="title">{titleText}</div>
        <div className="review-text">{reviewText}</div>
        <div className="text" onScroll={handleScroll}>
          <div>
            <Markdown>{terms}</Markdown>
          </div>
          <div className="filler" ref={endRef} />
        </div>
        <div className="agree-conditions">{agreeText}</div>
        {hasError && <div className="error-message">An unexpected error occured, please try again.</div>}
        <div className="action-section">
          <IconButton
            className="download"
            color="primary"
            variant="contained"
            component="a"
            target="_blank"
            href={`https://assets.ompnt.com/terms/${fileName}.v${termsVersion}.pdf`}
            download
            size="large"
          >
            <CloudDownload />
          </IconButton>
          <IconButton
            className="scroll-down"
            color="primary"
            variant="contained"
            onClick={scrollToBottom}
            size="large"
          >
            <KeyboardArrowDown />
          </IconButton>
          <Button variant="contained" size="large" onClick={logout}>
            {denyText}
          </Button>
          <Button disabled={!scrolledToBottom} className="accept-button" color="primary" variant="contained" size="large" onClick={accept}>
            {acceptText}
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
}

ApproveAccount.propTypes = {
  terms: PropTypes.string,
  termsLicense: PropTypes.string,
  termsVersion: PropTypes.number,
  account: PropTypes.string.isRequired,
  acceptTermsAndConditions: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
}
